import { createApiClient } from '../../../services/api/apiClient';
import type { AxiosError } from 'axios';
import {
  ScanConfiguration,
  ScanResult,
  UserFilter,
  UserMatch,
  BatchGenerationStartRequest,
  BatchGenerationProgress,
  BatchGenerationResult,
  MatchingStatistics,
  ApiResponse,
  SharePointFolder,
  ScanSessionMetadata,
  FolderAssignmentsResponse,
  FolderAssignmentRequest,
  MatchUpdatePayload,
  GenerationError,
  GenerationSuccess,
  GenerationSkip,
  PlaceholderScanResult,
} from '../types';
import { User } from '../../../components/shared/UserBrowser';

class BatchProfileApi {
  private async getApiClient(token: string) {
    const defaultTimeout = 120000; // 2 minutes baseline for long-running batch ops
    const configuredTimeout = Number(process.env.REACT_APP_API_TIMEOUT);
    const timeout = Number.isFinite(configuredTimeout) && configuredTimeout > 0
      ? Math.max(defaultTimeout, configuredTimeout)
      : defaultTimeout;

    const { client } = createApiClient(
      process.env.REACT_APP_API_URL || 'http://localhost:9110',
      token,
      {
        timeout,
        enableRetry: true,
        enableLogging: process.env.NODE_ENV === 'development'
      }
    );

    return client;
  }

  private normalizeGenerationError(raw: any): GenerationError {
    return {
      userId: raw?.user_id ?? '',
      userName: raw?.user_name ?? undefined,
      error: raw?.error ?? 'Unknown error',
      stage: raw?.stage ?? 'failed',
      timestamp: raw?.timestamp ? new Date(raw.timestamp) : new Date(),
      retryable: Boolean(raw?.retryable)
    };
  }

  private normalizeGenerationSuccess(raw: any): GenerationSuccess {
    return {
      userId: raw?.user_id ?? '',
      userName: raw?.user_name ?? undefined,
      profileId: raw?.profile_id ?? raw?.user_id ?? '',
      documentsProcessed: raw?.documents_processed ?? 0,
      processingTime: raw?.processing_time ?? 0,
      timestamp: raw?.timestamp ? new Date(raw.timestamp) : new Date()
    };
  }

  private normalizeGenerationSkip(raw: any): GenerationSkip {
    return {
      userId: raw?.user_id ?? '',
      userName: raw?.user_name ?? undefined,
      reason: raw?.reason ?? 'Skipped',
      timestamp: raw?.timestamp ? new Date(raw.timestamp) : new Date()
    };
  }

  private normalizeGenerationProgress(raw: any): BatchGenerationProgress {
    return {
      sessionId: raw?.session_id ?? '',
      total: raw?.total ?? 0,
      completed: raw?.completed ?? 0,
      failed: raw?.failed ?? 0,
      skipped: raw?.skipped ?? 0,
      current: raw?.current
        ? {
            userId: raw.current.user_id ?? '',
            userName: raw.current.user_name ?? undefined,
            status: raw.current.status ?? 'pending'
          }
        : undefined,
      averageProcessingTime: raw?.average_processing_time ?? undefined,
      errors: Array.isArray(raw?.errors)
        ? raw.errors.map((err: any) => this.normalizeGenerationError(err))
        : [],
      startedAt: raw?.started_at ? new Date(raw.started_at) : new Date(),
      estimatedCompletion: raw?.estimated_completion ? new Date(raw.estimated_completion) : undefined
    };
  }

  private normalizeGenerationResult(raw: any): BatchGenerationResult {
    const summary = raw?.summary ?? {};

    return {
      sessionId: raw?.session_id ?? '',
      completed: Array.isArray(raw?.completed)
        ? raw.completed.map((entry: any) => this.normalizeGenerationSuccess(entry))
        : [],
      failed: Array.isArray(raw?.failed)
        ? raw.failed.map((entry: any) => this.normalizeGenerationError(entry))
        : [],
      skipped: Array.isArray(raw?.skipped)
        ? raw.skipped.map((entry: any) => this.normalizeGenerationSkip(entry))
        : [],
      summary: {
        total: summary.total ?? 0,
        completed: summary.completed ?? 0,
        failed: summary.failed ?? 0,
        skipped: summary.skipped ?? 0,
        totalProcessingTime: summary.total_processing_time ?? 0,
        averageProcessingTime: summary.average_processing_time ?? 0,
        processingTime: summary.processing_time ?? undefined,
        completedAt: summary.completed_at ? new Date(summary.completed_at) : undefined
      },
      details: Array.isArray(raw?.details)
        ? raw.details.map((detail: any) => ({
            userId: detail?.user_id ?? '',
            userName: detail?.user_name ?? undefined,
            status: detail?.status ?? 'pending',
            error: detail?.error ?? undefined,
            processingTime: detail?.processing_time ?? undefined,
            profileUrl: detail?.profile_url ?? undefined,
            documentsProcessed: detail?.documents_processed ?? undefined,
            attempts: detail?.attempts ?? undefined
          }))
        : []
    };
  }

  // SharePoint Scanning Operations
  async startScan(
    config: ScanConfiguration,
    token: string
  ): Promise<ApiResponse<ScanSessionMetadata>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.post<ApiResponse<ScanSessionMetadata>>(
        '/api/batch-profiles/scan/start',
        config
      );
      return response.data;
    } catch (error) {
      console.error('Error starting SharePoint scan:', error);
      throw new Error('Failed to start SharePoint scan');
    }
  }

  async getScanStatus(sessionId: string, token: string): Promise<ApiResponse<ScanResult>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get<ApiResponse<ScanResult>>(
        `/api/batch-profiles/scan/${sessionId}/status`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching scan status:', error);
      throw new Error('Failed to fetch scan status');
    }
  }

  async cancelScan(sessionId: string, token: string): Promise<ApiResponse<void>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.post<ApiResponse<void>>(
        `/api/batch-profiles/scan/${sessionId}/cancel`
      );
      return response.data;
    } catch (error) {
      console.error('Error cancelling scan:', error);
      throw new Error('Failed to cancel scan');
    }
  }

  // User Filtering and Matching Operations
  async getFilteredUsers(
    filter: UserFilter,
    token: string,
    options?: { scanSessionId?: string; cacheKey?: string }
  ): Promise<ApiResponse<User[]>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.post<ApiResponse<User[]>>(
        '/api/batch-profiles/users/filter',
        {
          user_filters: filter,
          ...(options?.scanSessionId ? { scanSessionId: options.scanSessionId } : {}),
          ...(options?.cacheKey ? { cacheKey: options.cacheKey } : {})
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching filtered users:', error);
      throw new Error('Failed to fetch filtered users');
    }
  }

  async matchUsersToFolders(
    folders: SharePointFolder[],
    filter: UserFilter,
    token: string,
    options?: { scanSessionId?: string; cacheKey?: string }
  ): Promise<ApiResponse<UserMatch[]>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.post<ApiResponse<UserMatch[]>>(
        '/api/batch-profiles/match',
        {
          folders,
          user_filter: filter,
          ...(options?.scanSessionId ? { scan_session_id: options.scanSessionId } : {}),
          ...(options?.cacheKey ? { cache_key: options.cacheKey } : {})
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error matching users to folders:', error);
      throw new Error('Failed to match users to folders');
    }
  }

  async getAssignments(
    token: string,
    options?: { scanSessionId?: string; cacheKey?: string }
  ): Promise<ApiResponse<FolderAssignmentsResponse>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get<ApiResponse<FolderAssignmentsResponse>>(
        '/api/batch-profiles/assignments',
        {
          params: {
            ...(options?.scanSessionId ? { scanSessionId: options.scanSessionId } : {}),
            ...(options?.cacheKey ? { cacheKey: options.cacheKey } : {})
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching folder assignments:', error);
      throw new Error('Failed to load folder assignments');
    }
  }

  async getSessionMatches(
    token: string,
    scanSessionId: string
  ): Promise<ApiResponse<UserMatch[]>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get<ApiResponse<UserMatch[]>>(
        '/api/batch-profiles/matches',
        {
          params: { scanSessionId }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching session matches:', error);
      throw new Error('Failed to load session matches');
    }
  }

  async assignFolder(
    request: FolderAssignmentRequest,
    token: string
  ): Promise<ApiResponse<{ preference: Record<string, unknown> }>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.post<ApiResponse<{ preference: Record<string, unknown> }>>(
        '/api/batch-profiles/folder/assign',
        {
          user_id: request.userId,
          folder_path: request.folderPath,
          folder_name: request.folderName,
          site_id: request.siteId,
          site_key: request.siteKey,
          drive_id: request.driveId,
          web_url: request.webUrl
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error assigning folder:', error);
      const err = error as AxiosError<{ detail?: string }>;
      const detail = err.response?.data?.detail;
      throw new Error(detail || 'Failed to assign folder to user');
    }
  }

  async unassignFolder(userId: string, token: string): Promise<ApiResponse<void>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.post<ApiResponse<void>>(
        '/api/batch-profiles/folder/unassign',
        { user_id: userId }
      );
      return response.data;
    } catch (error) {
      console.error('Error unassigning folder:', error);
      throw new Error('Failed to remove folder assignment');
    }
  }

  async updateMatch(
    matchId: string,
    updates: MatchUpdatePayload,
    token: string
  ): Promise<ApiResponse<UserMatch>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.put<ApiResponse<UserMatch>>(
        `/api/batch-profiles/matches/${matchId}`,
        {
          scanSessionId: updates.scanSessionId,
          userId: updates.userId,
          isApproved: updates.isApproved,
          isRejected: updates.isRejected,
          manuallyVerified: updates.manuallyVerified,
          notes: updates.notes
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating match:', error);
      throw new Error('Failed to update match');
    }
  }

  async bulkUpdateMatches(
    updates: MatchUpdatePayload[],
    token: string,
    scanSessionId: string
  ): Promise<ApiResponse<UserMatch[]>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.post<ApiResponse<UserMatch[]>>(
        '/api/batch-profiles/matches/bulk-update',
        {
          scanSessionId,
          updates: updates.map((update) => ({
            matchId: update.matchId,
            userId: update.userId,
            isApproved: update.isApproved,
            isRejected: update.isRejected,
            manuallyVerified: update.manuallyVerified,
            notes: update.notes
          }))
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error applying bulk updates:', error);
      throw new Error('Failed to update matches');
    }
  }

  async getMatchingStatistics(
    matches: UserMatch[],
    token: string
  ): Promise<ApiResponse<MatchingStatistics>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.post<ApiResponse<MatchingStatistics>>(
        '/api/batch-profiles/matches/statistics',
        { matches }
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching matching statistics:', error);
      throw new Error('Failed to fetch matching statistics');
    }
  }

  // Batch Generation Operations
  async startBatchGeneration(
    request: BatchGenerationStartRequest,
    token: string
  ): Promise<ApiResponse<{ sessionId: string; total: number }>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.post<ApiResponse<{ sessionId: string; total: number }>>(
        '/api/batch-profiles/generate/start',
        request
      );
      return response.data;
    } catch (error) {
      console.error('Error starting batch generation:', error);
      throw new Error('Failed to start batch generation');
    }
  }

  async getBatchGenerationStatus(
    sessionId: string,
    token: string
  ): Promise<ApiResponse<BatchGenerationProgress>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get<ApiResponse<any>>(
        `/api/batch-profiles/generate/${sessionId}/status`
      );

      const payload = response.data;
      if (payload?.data) {
        return {
          ...payload,
          data: this.normalizeGenerationProgress(payload.data)
        } as ApiResponse<BatchGenerationProgress>;
      }

      return payload as ApiResponse<BatchGenerationProgress>;
    } catch (error) {
      console.error('Error fetching batch generation status:', error);
      throw new Error('Failed to fetch batch generation status');
    }
  }

  async getBatchGenerationResult(
    sessionId: string,
    token: string
  ): Promise<ApiResponse<BatchGenerationResult>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get<ApiResponse<any>>(
        `/api/batch-profiles/generate/${sessionId}/result`
      );

      const payload = response.data;
      if (payload?.data) {
        return {
          ...payload,
          data: this.normalizeGenerationResult(payload.data)
        } as ApiResponse<BatchGenerationResult>;
      }

      return payload as ApiResponse<BatchGenerationResult>;
    } catch (error) {
      console.error('Error fetching batch generation result:', error);
      throw new Error('Failed to fetch batch generation result');
    }
  }

  async cancelBatchGeneration(
    sessionId: string,
    token: string
  ): Promise<ApiResponse<void>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.post<ApiResponse<void>>(
        `/api/batch-profiles/generate/${sessionId}/cancel`
      );
      return response.data;
    } catch (error) {
      console.error('Error cancelling batch generation:', error);
      throw new Error('Failed to cancel batch generation');
    }
  }

  async getGeneratedProfileMarkdown(
    userId: string,
    token: string
  ): Promise<string> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get<{ profile_markdown?: string }>(
        `/api/batch-profiles/profile/${userId}`
      );
      return response.data?.profile_markdown ?? '';
    } catch (error) {
      console.error('Error fetching generated profile content:', error);
      throw new Error('Failed to load generated profile');
    }
  }

  async retryGeneration(
    sessionId: string,
    userIds: string[],
    token: string,
    options?: { forceRegeneration?: boolean }
  ): Promise<ApiResponse<{ sessionId: string; users: string[] }>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.post<ApiResponse<{ sessionId: string; users: string[] }>>(
        `/api/batch-profiles/generate/${sessionId}/retry`,
        {
          user_ids: userIds,
          force_regeneration: options?.forceRegeneration ?? false
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error triggering regeneration:', error);
      throw new Error('Failed to trigger regeneration');
    }
  }

  async detectPlaceholderProfiles(
    sessionId: string,
    token: string,
    userIds?: string[]
  ): Promise<PlaceholderScanResult> {
    try {
      const client = await this.getApiClient(token);
      const payload = userIds && userIds.length > 0 ? { userIds } : {};
      const response = await client.post<ApiResponse<PlaceholderScanResult>>(
        `/api/batch-profiles/generate/${sessionId}/placeholder-scan`,
        payload
      );

      if (response.data?.success && response.data.data) {
        return {
          evaluated: response.data.data.evaluated ?? 0,
          placeholderUserIds: response.data.data.placeholderUserIds ?? [],
          missingProfiles: response.data.data.missingProfiles ?? [],
          unknownUserIds: response.data.data.unknownUserIds ?? [],
          errors: response.data.data.errors ?? [],
          message: response.data.data.message ?? null
        };
      }

      return {
        evaluated: 0,
        placeholderUserIds: [],
        missingProfiles: [],
        unknownUserIds: [],
        errors: [],
        message: null
      };
    } catch (error) {
      console.error('Error detecting placeholder profiles:', error);
      throw new Error('Failed to detect placeholder profiles');
    }
  }

  // SharePoint and Configuration Operations
  async getSharePointSites(token: string): Promise<ApiResponse<any[]>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get<ApiResponse<any[]>>('/api/sharepoint/sites');
      return response.data;
    } catch (error) {
      console.error('Error fetching SharePoint sites:', error);
      throw new Error('Failed to fetch SharePoint sites');
    }
  }

  async getSharePointLibraries(
    siteKey: string,
    token: string
  ): Promise<ApiResponse<any[]>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get<ApiResponse<any[]>>(
        `/api/sharepoint/sites/${siteKey}/libraries`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching SharePoint libraries:', error);
      throw new Error('Failed to fetch SharePoint libraries');
    }
  }

  async getDefaultConfiguration(token: string): Promise<ApiResponse<ScanConfiguration>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get<ApiResponse<ScanConfiguration>>(
        '/api/batch-profiles/config/default'
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching default configuration:', error);
      throw new Error('Failed to fetch default configuration');
    }
  }

  // Utility Methods
  async validateConfiguration(
    config: ScanConfiguration,
    token: string
  ): Promise<ApiResponse<{ isValid: boolean; errors: string[] }>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.post<ApiResponse<{ isValid: boolean; errors: string[] }>>(
        '/api/batch-profiles/config/validate',
        config
      );
      return response.data;
    } catch (error) {
      console.error('Error validating configuration:', error);
      throw new Error('Failed to validate configuration');
    }
  }

  async getSystemStatus(token: string): Promise<ApiResponse<{
    sharepoint: boolean;
    entra: boolean;
    storage: boolean;
    llm: boolean;
  }>> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get<ApiResponse<{
        sharepoint: boolean;
        entra: boolean;
        storage: boolean;
        llm: boolean;
      }>>('/api/batch-profiles/system/status');
      return response.data;
    } catch (error) {
      console.error('Error fetching system status:', error);
      throw new Error('Failed to fetch system status');
    }
  }

  // Export functionality
  async exportResults(
    sessionId: string,
    format: 'csv' | 'xlsx' | 'json',
    token: string
  ): Promise<Blob> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get(
        `/api/batch-profiles/export/${sessionId}/${format}`,
        {
          responseType: 'blob'
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error exporting results:', error);
      throw new Error('Failed to export results');
    }
  }

  async getUserDocuments(
    userId: string,
    sessionId: string,
    token: string
  ): Promise<{
    userId: string;
    userName?: string;
    folderPath?: string;
    documents: Array<{
      name: string;
      path: string;
      extractedText: string;
      size?: number;
      lastModified?: Date;
      type?: string;
    }>;
    totalDocuments: number;
    extractionTimestamp?: Date;
  }> {
    try {
      const client = await this.getApiClient(token);
      const response = await client.get(
        `/api/batch-profiles/generation/${sessionId}/users/${userId}/documents`
      );

      const data = response.data;
      return {
        userId: data.user_id || userId,
        userName: data.user_name,
        folderPath: data.folder_path,
        documents: (data.documents || []).map((doc: any) => ({
          name: doc.name || doc.file_name || '',
          path: doc.path || doc.file_path || '',
          extractedText: doc.extracted_text || doc.content || '',
          size: doc.size,
          lastModified: doc.last_modified ? new Date(doc.last_modified) : undefined,
          type: doc.type || doc.file_type
        })),
        totalDocuments: data.total_documents || (data.documents?.length || 0),
        extractionTimestamp: data.extraction_timestamp ? new Date(data.extraction_timestamp) : undefined
      };
    } catch (error) {
      const axiosError = error as AxiosError<{ detail?: string }>;
      throw new Error(
        axiosError.response?.data?.detail ||
        axiosError.message ||
        'Failed to fetch document details'
      );
    }
  }

  // WebSocket connection helper
  createWebSocketConnection(
    sessionId: string,
    token: string,
    type: 'scan' | 'generation'
  ): WebSocket {
    const wsUrl = `${process.env.REACT_APP_WS_URL || 'ws://localhost:9110'}/api/batch-profiles/ws/${type}/${sessionId}`;
    const ws = new WebSocket(wsUrl, ['bearer', token]);

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return ws;
  }
}

export const batchProfileApi = new BatchProfileApi();
